import { logger } from '@/utils/logger';
import { SUGGESTION_LIVE, SUGGESTION_LOCAL } from '@/constants/index';
import { Suggestion, SuggestionLocal, SuggestionsResponse } from './suggestionsInterface';
import { sanitizerChars } from '@/utils/utils-url';
import { httpClient } from '../../../../../search-library/src/utils/http';

const getSearchHistoryKey = () => `fss.searchHistory.${'de_DE'}`;
const SUGGESTION_TOTAL_LIMIT = 6;
const SUGGESTION_LOCAL_LIMIT = 3;

class Suggestions {
  searchProfile!: string;
  savedSearches: Array<SuggestionLocal> = [];

  init(searchProfile: string) {
    const localStorageContents = window.localStorage.getItem(getSearchHistoryKey());
    this.searchProfile = searchProfile;
    logger.log('searchProfile', searchProfile, localStorageContents);

    if (localStorageContents) {
      this.savedSearches = JSON.parse(localStorageContents);
    }
  }

  getAllLocalSuggestions(): Suggestion[] {
    return this.savedSearches.map((search) => ({
      ...search,
      type: SUGGESTION_LOCAL,
      value: sanitizerChars(search.raw),
      default: true,
    }));
  }

  // eslint-disable-next-line class-methods-use-this
  fetchSuggestions(query: string): Promise<Suggestion[]> {
    const params = { limit: SUGGESTION_TOTAL_LIMIT, query, searchProfile: this.searchProfile };

    return httpClient
      .get<SuggestionsResponse>('/suggestions', { params })
      .then(({ data: suggestionsResponse }) => {
        // find matching suggestions from local history and assign proper type
        let localSuggestions = this.savedSearches
          .filter((search) => search.raw.toLowerCase().startsWith(suggestionsResponse.prefixTerm.toLowerCase().trim()))
          .map((search) => ({
            ...search,
            type: SUGGESTION_LOCAL,
          }));

        // remove duplicated from live results if they exist locally and assign proper type
        const liveSuggestions = suggestionsResponse.suggests
          .filter(
            (suggestion: string) => !localSuggestions.some((x) => x.raw.toLowerCase() === suggestion.toLowerCase()),
          )
          .map((suggestion: string) => ({
            type: SUGGESTION_LIVE,
            raw: sanitizerChars(suggestion),
            isSuggest: true,
            default: false,
          }));

        // limit amount of local suggestions to a max (to be able to show suggestions from server which are supposed to drive user behavior)
        localSuggestions = localSuggestions.slice(
          0,
          Math.max(SUGGESTION_LOCAL_LIMIT, SUGGESTION_TOTAL_LIMIT - liveSuggestions.length),
        );

        const highlightRegex = new RegExp(`(${suggestionsResponse.prefixTerm})(.*)$`, 'ig');

        // merge local and live suggestions into one list
        const highlightedSuggestions: Suggestion[] = localSuggestions
          .concat(liveSuggestions)
          .slice(0, SUGGESTION_TOTAL_LIMIT)
          .map((suggestion) => ({
            ...suggestion,
            value: suggestion.raw.replace(highlightRegex, '<b>$1</b>$2'),
          }));

        logger.debug('fetchSuggestion', highlightedSuggestions, suggestionsResponse);

        return highlightedSuggestions;
      })
      .catch((e) => {
        logger.error(e);
        return Promise.reject(e);
      });
  }

  removeSuggestion(suggestion: Suggestion) {
    const indexToRemove = this.savedSearches.findIndex((search) => search.raw === suggestion.raw);

    if (indexToRemove > -1) {
      this.savedSearches.splice(indexToRemove, 1);
      this.persist();
    }

    logger.debug('Suggestion removed', suggestion);
  }

  isSuggestionValid(suggestion: SuggestionLocal) {
    return suggestion && typeof suggestion === 'object' && !suggestion.raw;
  }

  addSuggestion(suggestion: SuggestionLocal) {
    if (this.isSuggestionValid(suggestion)) {
      throw new Error(`Suggestion ${suggestion} is not valid.`);
    }

    const isSaved = this.savedSearches.some((search) => search.raw === suggestion.raw.trim());

    if (isSaved) {
      return;
    }

    this.savedSearches.unshift(suggestion);
    this.persist();
  }

  persist() {
    this.savedSearches = this.savedSearches.slice(0, SUGGESTION_TOTAL_LIMIT);

    window.localStorage.setItem(getSearchHistoryKey(), JSON.stringify(this.savedSearches));
  }
}

export const suggestions = new Suggestions();
